import React, { Fragment, useCallback, useEffect, useState } from "react";
import IntlMessages from "Util/IntlMessages";
import {
  Row,
  Card,
  CardBody,
  CardTitle,
  Progress,
  Button,
  Table,
  Col,
  CardHeader
} from "reactstrap";
import TodoList from "Components/TodoList";
import { NavLink } from "react-router-dom";
import PerfectScrollbar from "react-perfect-scrollbar";
import { Separator } from "Components/CustomBootstrap";
import BreadcrumbContainer from "Components/BreadcrumbContainer";
import { LineShadow, BarShadow } from "Components/Charts";
import {
  lineChartConfig,
  getBarChartConfigSingle,
  productCategoriesBarDemo
} from "Constants/chartConfig";
import TwoLevelPieChart from "Components/Rechart/TwoLevelPieChart";
import TinyLineChart from "Components/Rechart/TinyLine";
import ReactTable from "react-table";
import Chart from "chart.js";
import DataTablePagination from "Components/DataTables/pagination";
import { ReactSortable } from "react-sortablejs";
import RadarMultipleSeries from "Components/ApexCharts/RadarMultipleSeries";

import "chartjs-plugin-datalabels";
import "react-circular-progressbar/dist/styles.css";
import "react-perfect-scrollbar/dist/css/styles.css";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-table/react-table.css";
import Weather from "Components/Weather/Weather";
import WeatherSVG from "Components/Weather/WeatherSVG";

import eventsData from "Data/events.json";
import messagesData from "Data/messages.json";
import popularitemsData from "Data/popularitems.json";
import recentCustomers from "Data/recentCustomers.json";
import recentActivities from "Data/recentActivities.json";
import productsData from "Data/products.json";
import profileStatusData from "Data/profileStatus.json";
import {
  Sparklines,
  SparklinesLine,
  SparklinesCurve,
  SparklinesBars,
  SparklinesSpots
} from "react-sparklines";
import {
  MAIN_SPARKLINE_LINE_RED,
  MAIN_SPARKLINE_LINE_THEME,
  MAIN_SPARKLINE_BARS_PURPLE,
  MAIN_SPARKLINE_LINE_THEME_FILLED,
  MAIN_SPARKLINE_SPOTS_INCOME
} from "Constants/mainDashboardSparklines";

{
  const g = Chart.defaults.global as typeof Chart.defaults.global & {
    plugins?: { datalabels?: { display?: boolean } };
  };
  g.plugins = g.plugins ?? {};
  g.plugins.datalabels = g.plugins.datalabels ?? {};
  g.plugins.datalabels.display = false;
}
const sparklineData = [936, 968, 1025, 999, 998, 1014, 1017, 1010, 1010, 1007];
const sparklineWeek = [936, 968, 936, 930, 998, 1014, 1014, 1010, 900, 1007];
const sparklineMonth = [1000, 968, 900, 920, 950, 1014, 1017, 1010, 930, 1007];
const sparklineYear = [857, 870, 930, 999, 998, 930, 1017, 1070, 1030, 1007];
const sparklineBar = [5, 6, 7, 2, 0, -4, -2, 4];
const sparklineIncome = [1, 1, 0, 1, 2, 3, 1, 4, 2, 6, 1, 1];
function boxMullerRandom() {
  let phase = false;
  let x1 = 0;
  let x2 = 0;
  let w = 0;

  return (function() {
    if ((phase = !phase)) {
      do {
        x1 = 2.0 * Math.random() - 1.0;
        x2 = 2.0 * Math.random() - 1.0;
        w = x1 * x1 + x2 * x2;
      } while (w >= 1.0);

      w = Math.sqrt((-2.0 * Math.log(w)) / w);
      return x1 * w;
    } else {
      return x2 * w;
    }
  })();
}

function randomData(n = 30) {
  return Array.apply(0, Array(n)).map(boxMullerRandom);
}

type TableCellProps = { value?: React.ReactNode };

const dataTableColumns = [
  {
    Header: "Invoice",
    accessor: "invoice",
    Cell: (props: TableCellProps) => (
      <p className="list-item-heading">{props.value}</p>
    )
  },
  {
    Header: "User",
    accessor: "username",
    Cell: (props: TableCellProps) => (
      <p className="text-muted">{props.value}</p>
    )
  },
  {
    Header: "Order Date",
    accessor: "orderDate",
    Cell: (props: TableCellProps) => (
      <p className="text-muted">{props.value}</p>
    )
  },
  {
    Header: "Amount",
    accessor: "amount",
    Cell: (props: TableCellProps) => (
      <p className="text-muted">{props.value}</p>
    )
  },
  {
    Header: "Status",
    accessor: "status",
    Cell: (props: TableCellProps) => (
      <span className="badge badge-outline-theme-1 fontsize10px pr-1 pl-1 mt-2 w-100px">
        {props.value}
      </span>
    )
  },
  {
    Header: "Tracking Number",
    accessor: "trackingNumber",
    Cell: (props: TableCellProps) => (
      <p className="text-muted">{props.value}</p>
    )
  }
];
const itemsDesc = productsData.data.slice(0, 6);
const messagesD = messagesData.data;
const popularitem = popularitemsData.data;
const recentCustomer = recentCustomers.data;
const recentAct = recentActivities.data;
const events = eventsData.data;
const dataTableData = productsData.data.slice(0, 12);
const profileStatuses = profileStatusData.data;

const MAIN_SCROLL_OPTIONS = { suppressScrollX: true, wheelPropagation: false };

function DefaultDashboard() {
  const [liveSparkData, setLiveSparkData] = useState<number[]>([]);
  const [sortableSalesCards, setSortableSalesCards] = useState([
    { id: "todays" },
    { id: "weekly" },
    { id: "monthly" },
    { id: "yearly" }
  ]);

  useEffect(() => {
    const id = setInterval(() => {
      setLiveSparkData(d => d.concat([boxMullerRandom()]));
    }, 100);
    return () => clearInterval(id);
  }, []);

  const renderSalesSortableCard = useCallback((item: { id: string }) => {
    switch (item.id) {
      case "todays":
        return (
          <Card>
            <CardHeader className="p-0 position-relative">
              <div className="position-absolute handle card-icon">
                <i className="simple-icon-shuffle" />
              </div>
            </CardHeader>
            <CardBody className="d-flex justify-content-between align-items-center">
              <CardTitle className="mb-0 mt-2">
                <IntlMessages id="main.todays" />{" "}
                <span className="fa fa-level-down-alt text-danger" />
                <p>
                  {" "}
                  <span className="bg-danger bold-fonts  mb-0 fontsize10px pr-1 pl-1 text-white text-uppercase">
                    <IntlMessages id="main.sales-progress" />
                  </span>
                </p>
              </CardTitle>{" "}
              <div className="float-right mt-3">
                <sup className="text-danger float-right">-10</sup>{" "}
                <span>$150K</span>
                <sup>
                  <Sparklines data={sparklineData} margin={6}>
                    <SparklinesLine style={MAIN_SPARKLINE_LINE_RED} />
                  </Sparklines>
                </sup>
              </div>
            </CardBody>
          </Card>
        );
      case "weekly":
        return (
          <Card>
            <CardHeader className="p-0 position-relative">
              <div className="position-absolute handle card-icon">
                <i className="simple-icon-shuffle" />
              </div>
            </CardHeader>
            <CardBody className="d-flex justify-content-between align-items-center">
              <CardTitle className="mb-0 mt-2">
                <IntlMessages id="main.weekly" />
                <span className="fa fa-level-down-alt font-color-1" />
                <p>
                  {" "}
                  <span className="bg-primary bold-fonts  mb-0 fontsize10px pr-1 pl-1 text-white text-uppercase">
                    <IntlMessages id="main.sales-progress" />
                  </span>
                </p>
              </CardTitle>
              <div className="float-right mt-3">
                <sup className="font-color-1 float-right">+15</sup>{" "}
                <span>$963K</span>
                <sup>
                  <Sparklines data={sparklineWeek} margin={6}>
                    <SparklinesLine style={MAIN_SPARKLINE_LINE_THEME} />
                  </Sparklines>
                </sup>
              </div>
            </CardBody>
          </Card>
        );
      case "monthly":
        return (
          <Card>
            <CardHeader className="p-0 position-relative">
              <div className="position-absolute handle card-icon">
                <i className="simple-icon-shuffle" />
              </div>
            </CardHeader>
            <CardBody className="d-flex justify-content-between align-items-center">
              <CardTitle className="mb-0 mt-2">
                <IntlMessages id="main.monthly" />{" "}
                <span className="fa fa-level-up-alt text-danger" />
                <p>
                  {" "}
                  <span className="bg-danger bold-fonts  mb-0 fontsize10px pr-1 pl-1 text-white text-uppercase">
                    <IntlMessages id="main.sales-progress" />
                  </span>
                </p>
              </CardTitle>{" "}
              <div className="float-right mt-3">
                <sup className="float-right text-danger">-30</sup>{" "}
                <span>$3610K</span>
                <sup>
                  <Sparklines data={sparklineMonth} margin={6}>
                    <SparklinesLine style={MAIN_SPARKLINE_LINE_RED} />
                  </Sparklines>
                </sup>
              </div>
            </CardBody>
          </Card>
        );
      case "yearly":
        return (
          <Card>
            <CardHeader className="p-0 position-relative">
              <div className="position-absolute handle card-icon">
                <i className="simple-icon-shuffle" />
              </div>
            </CardHeader>
            <CardBody className="d-flex justify-content-between align-items-center">
              <CardTitle className="mb-0 mt-2">
                <IntlMessages id="main.yearly" />{" "}
                <span className="fa fa-level-up-alt font-color-1" />
                <p>
                  {" "}
                  <span className="bg-primary bold-fonts  mb-0 fontsize10px pr-1 pl-1 text-white text-uppercase">
                    <IntlMessages id="main.sales-progress" />
                  </span>
                </p>
              </CardTitle>
              <div className="float-right mt-3">
                <sup className="font-color-1 float-right">+7</sup>{" "}
                <span>$14908K</span>
                <sup>
                  <Sparklines data={sparklineYear} margin={6}>
                    <SparklinesLine style={MAIN_SPARKLINE_LINE_THEME} />
                  </Sparklines>
                </sup>
              </div>
            </CardBody>
          </Card>
        );
      default:
        return null;
    }
  }, []);

  return (
    <Fragment>
        <Row>
          <Col xs="12">
            <BreadcrumbContainer heading={<IntlMessages id="menu.default" />} />
            <Separator className="mb-5" />
          </Col>
        </Row>
        {/* First Row starts here  */}
        <Row>
          <Col lg="3" sm="6" xs="12">
            <Card className="mb-4">
              <CardBody>
                <i className="float-left fa fa-cart-plus text-success mt-1 font18px" />
                <div className="ml-5">
                  <p className="text-muted font-weight-semibold mb-2">
                    <IntlMessages id="main.weekly-sales" />
                  </p>
                  <p className="lead text-muted bold">1345</p>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col lg="3" sm="6" xs="12">
            <Card className="mb-4">
              <CardBody>
                <i className="float-left fa fa-chart-line mt-1 font18px text-info" />
                <div className="ml-5">
                  <p className="text-muted font-weight-semibold mb-2">
                    <IntlMessages id="main.monthly-visits" />
                  </p>
                  <p className="lead text-muted bold">687,123</p>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col lg="3" sm="6" xs="12">
            <Card className="mb-4">
              <CardBody>
                <i className="float-left fa fa-shopping-bag mt-1 font18px text-danger" />
                <div className="ml-5">
                  <p className="text-muted font-weight-semibold mb-2">
                    <IntlMessages id="main.items-for-sale" />
                  </p>
                  <p className="lead text-muted bold">1054</p>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col lg="3" sm="6" xs="12">
            <Card className="mb-4">
              <CardBody>
                <i className="float-left fa fa-users mt-1 font18px text-warning" />
                <div className="ml-5">
                  <p className="text-muted font-weight-semibold mb-2">
                    <IntlMessages id="main.users" />
                  </p>
                  <p className="lead text-muted bold">505,652</p>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
        {/* Small Divs */}

        {/* Second Row starts here */}

        {/* Line Chart starts here */}
        <Row>
          <Col lg="8" md="12" sm="12" xs="12" className="mb-4">
            <Card>
              <CardBody>
                <CardTitle>
                  <IntlMessages id="main.sales" />
                </CardTitle>
                <div className="dashboard-line-chart">
                  <LineShadow {...lineChartConfig} />
                </div>
              </CardBody>
            </Card>
          </Col>
          {/* Line Chart Ends Here */}

          <Col lg="4" xl="4" sm="12" className="mb-4">
            <Card>
              <div className="position-absolute card-top-buttons">
                <button className="btn btn-header-light icon-button">
                  <i className="simple-icon-refresh" />
                </button>
              </div>
              <CardBody>
                <CardTitle>
                  <IntlMessages id="main.statistics" />
                </CardTitle>
                <div className="clearfix">
                  <div className="float-left font15px">
                    <IntlMessages id="main.traffic" />
                  </div>
                  <div className="float-right">
                    <sup className="text-success">+31</sup> <span>500K</span>
                    <sup>
                      <Sparklines data={liveSparkData} limit={20}>
                        <SparklinesLine color="#922c88" />
                        <SparklinesSpots />
                      </Sparklines>
                    </sup>
                  </div>
                </div>

                {/* Traffic Ends here */}

                <hr className="border" />

                {/* Conversion Starts here */}
                <div className="clearfix">
                  <div className="float-left font15px">
                    <IntlMessages id="main.conversion" />
                  </div>
                  <div className="float-right">
                    <sup className="text-danger">-8</sup> <span>$150K</span>
                    <sup>
                      <Sparklines data={sparklineBar}>
                        <SparklinesBars style={MAIN_SPARKLINE_BARS_PURPLE} />
                      </Sparklines>
                    </sup>
                  </div>
                </div>
                {/* Conversion Ends here */}
                <hr className="border" />
                {/* Bounce Rate starts here */}
                <div className="clearfix">
                  <div className="float-left font15px">
                    <IntlMessages id="main.bounce-rate" />
                  </div>
                  <div className="float-right">
                    <sup className="text-success">+21%</sup> <span>5K</span>
                    <sup>
                      <Sparklines data={sparklineData} margin={6}>
                        <SparklinesLine style={MAIN_SPARKLINE_LINE_THEME} />
                      </Sparklines>
                    </sup>
                  </div>
                </div>
                {/* Bounce Rate ends here */}
                <hr className="border" />
                {/* Income starts here */}
                <div className="clearfix">
                  <div className="float-left font15px">
                    <IntlMessages id="main.income" />
                  </div>
                  <div className="float-right">
                    <sup className="text-danger">-23</sup> <span>430K</span>
                    <sup>
                      <Sparklines data={sparklineIncome} margin={6}>
                        <SparklinesLine style={MAIN_SPARKLINE_LINE_THEME_FILLED} />
                        <SparklinesSpots
                          size={4}
                          style={MAIN_SPARKLINE_SPOTS_INCOME}
                        />
                      </Sparklines>
                    </sup>
                  </div>
                </div>

                <hr className="border" />
                {/* Income ends here */}
              </CardBody>
            </Card>
          </Col>
        </Row>
        {/* Second Row Ends Here  */}

        {/*  Table Starts Here */}
        <Row>
          <Col xl="12" lg="12" className="mb-4">
            <Card className="h-100">
              <CardBody>
                <CardTitle>
                  <IntlMessages id="main.order-status" />
                </CardTitle>
                <div className="dashboard-order-toolbar d-flex flex-wrap align-items-center gap-2 mb-3">
                  <Button color="primary" size="sm" className="dashboard-toolbar-btn">
                    <i className="fa fa-plus-square-o me-1" aria-hidden />
                    Add
                  </Button>
                  <Button
                    color="primary"
                    size="sm"
                    className="dashboard-toolbar-btn"
                    aria-label="Print"
                  >
                    <i className="fa fa-print" aria-hidden />
                  </Button>
                  <Button color="primary" size="sm" className="dashboard-toolbar-btn">
                    <i className="fa fa-file-pdf-o me-1" aria-hidden />
                    Export PDF
                  </Button>
                  <div className="dashboard-toolbar-search flex-grow-1">
                    <input
                      type="text"
                      autoComplete="off"
                      className="form-control form-control-sm"
                      placeholder="Search"
                      id="demo-input-search2"
                    />
                  </div>
                </div>
                <ReactTable
                  defaultPageSize={6}
                  data={dataTableData}
                  columns={dataTableColumns}
                  minRows={0}
                  PaginationComponent={DataTablePagination}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Table Ends Here */}

        <Row className="align-items-stretch dashboard-three-col-row dashboard-three-col-compact">
          <Col lg="4" md="12" className="mb-4 d-flex">
            <Card className="w-100 dashboard-equal-height-card">
              <CardBody className="d-flex flex-column flex-grow-1 min-height-0">
                <CardTitle className="flex-shrink-0 mb-3">
                  <IntlMessages id="main.product-categories" />
                </CardTitle>
                <div className="dashboard-three-col-card-scroll flex-grow-1 min-height-0">
                  <PerfectScrollbar
                    option={MAIN_SCROLL_OPTIONS}
                  >
                    <div className="dashboard-bar-chart dashboard-bar-chart--compact flex-shrink-0">
                      <BarShadow {...getBarChartConfigSingle()} />
                    </div>
                    <ul className="dashboard-product-categories list-unstyled mb-0 mt-3">
                      {productCategoriesBarDemo.labels.map((name, index) => {
                        const v = productCategoriesBarDemo.values[index];
                        const total = productCategoriesBarDemo.values.reduce(
                          (a, b) => a + b,
                          0
                        );
                        const pct = Math.round((v / total) * 1000) / 10;
                        return (
                          <li
                            key={name}
                            className="d-flex justify-content-between align-items-baseline py-1 border-bottom text-small"
                          >
                            <span className="font-weight-medium text-truncate me-2">
                              {name}
                            </span>
                            <span className="text-muted text-nowrap flex-shrink-0">
                              {pct}% · {v}k
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                    <div className="dashboard-sales-goal-block pt-3 mt-2 flex-shrink-0">
                      <CardTitle className="mb-3">
                        <IntlMessages id="main.sales-goal" />
                      </CardTitle>
                      {profileStatuses.map((s, index) => {
                        return (
                          <div key={index} className="mb-4">
                            <p className="mb-2">
                              {s.title}
                              <span className="float-right text-muted">
                                {s.status}/{s.total}
                              </span>
                            </p>
                            <Progress value={(s.status / s.total) * 100} />
                          </div>
                        );
                      })}
                    </div>
                  </PerfectScrollbar>
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* Todo List */}

          <Col lg="4" md="12" className="mb-4 d-flex">
            <Card className="w-100 dashboard-equal-height-card">
              <CardBody className="d-flex flex-column flex-grow-1 min-height-0">
                <CardTitle className="flex-shrink-0 mb-3">
                  <IntlMessages id="main.tasks" />
                </CardTitle>
                <TodoList />
              </CardBody>
            </Card>
          </Col>

          {/* Messages */}
          <Col lg="4" md="12" className="mb-4 d-flex">
            <Card className="w-100 dashboard-equal-height-card">
              <CardBody className="d-flex flex-column flex-grow-1 min-height-0">
                <CardTitle className="flex-shrink-0 mb-3">
                  <IntlMessages id="main.team-messages" />

                  <div className="mt-2">
                    <span className=" bg-theme-1 mr-2 mb-2 pl-1 pr-1 fontsize10px">
                      <IntlMessages id="main.total-online" />
                    </span>
                    <span className="fontsize10px bg-danger text-white pl-1 pr-1">
                      <IntlMessages id="main.total-offline" />
                    </span>
                  </div>
                </CardTitle>
                <div className="dashboard-recent-act-list flex-grow-1 min-height-0">
                  <PerfectScrollbar
                    option={MAIN_SCROLL_OPTIONS}
                  >
                    {messagesD.map((message, index) => {
                      return (
                        <div
                          key={index}
                          className="d-flex flex-row mb-3 pb-3 border-bottom"
                        >
                          <NavLink
                            to={`/app/main/layout/team-message/${message.key}`}
                          >
                            <img
                              src={message.thumb}
                              alt={message.label}
                              className="img-thumbnail border-0 rounded-circle list-thumbnail align-self-center xsmall"
                            />

                            <p
                              className={`${
                                message.color
                              } fontsize10px pr-1 pl-1 mt-2 text-white`}
                            >
                              {message.online}
                            </p>
                          </NavLink>

                          <div className="pl-3 pr-2">
                            <div className="float-right">{message.time}</div>
                            <NavLink
                              to={`/app/main/layout/team-message/${message.key}`}
                            >
                              <p className="font-weight-medium mb-0 ">
                                {message.label}
                              </p>

                              <p className="text-muted mb-0 mt-1 text-small">
                                {message.message}
                              </p>
                            </NavLink>
                          </div>
                        </div>
                      );
                    })}
                  </PerfectScrollbar>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Recent Customers End here */}

        <ReactSortable
          list={sortableSalesCards}
          setList={setSortableSalesCards}
          handle=".handle"
          className="row"
        >
          {sortableSalesCards.map(item => (
            <Col key={item.id} xl="3" lg="6" className="mb-4">
              {renderSalesSortableCard(item)}
            </Col>
          ))}
        </ReactSortable>

        <Row>
          <Col sm="12" md="12" lg="6" xl="4" className="mb-4">
            <Card>
              <CardBody>
                <div className="text-center float-none-xs">
                  <div className="d-inline-block">
                    <h5 className="d-inline">
                      <IntlMessages id="main.weather-forecast" />
                    </h5>
                    <span className="text-muted text-small d-block">
                      <IntlMessages id="main.realtime-weather" />
                    </span>
                  </div>
                </div>
                <Row className="mx-auto">
                  <WeatherSVG />
                </Row>
              </CardBody>

              <div className="card-body pt-0">
                <Weather />
              </div>
            </Card>
          </Col>
          <Col lg="6" sm="12" md="12" xl="4" className="mb-4">
            <Card>
              <CardBody>
                <div className="pie-mini-minHeight">
                  <div className="text-center">
                    <div className="d-inline-block ">
                      <h5 className="d-inline">
                        <IntlMessages id="main.last-year-income" />
                      </h5>
                      <span className="text-muted text-small d-block">
                        <IntlMessages id="main.last-year-sales-report" />
                      </span>
                    </div>
                  </div>
                  <div className="py-3 mx-auto">
                    <Row>
                      <Col lg="2" md="4" sm="4" xs="3" />
                      <Col className="mt-3" lg="8" md="mx-auto" sm="6" xs="9">
                        <TwoLevelPieChart />
                      </Col>
                      <Col lg="2" md="3" sm="2" />
                    </Row>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* Tiny Line Chart Starts here */}

          <Col lg="6" sm="12" md="12" xl="4" className="mb-4">
            <Card>
              <CardBody>
                <div className="pie-mini-minHeight">
                  <div className="text-center">
                    <div className="d-inline-block ">
                      <h5 className="d-inline">
                        <IntlMessages id="main.last-year-income" />
                      </h5>
                      <span className="text-muted text-small d-block">
                        <IntlMessages id="main.last-year-sales-report" />
                      </span>
                    </div>
                  </div>
                  <div className="py-3 mx-auto">
                    <Row>
                      <Col lg="2" md="4" sm="4" xs="3" />
                      <Col className="mt-5" lg="8" md="mx-auto" sm="6" xs="9">
                        <TinyLineChart />
                      </Col>
                      <Col lg="2" md="3" sm="2" />
                    </Row>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
          {/* Tiny Line Chart Ends Here */}
        </Row>

        {/* Transactions Table Starts here */}

        <Row>
          <Col sm="12" lg="6" className="mb-4">
            <Card className="h-100">
              <CardBody>
                <CardTitle>
                  <IntlMessages id="main.popular-items" />
                </CardTitle>
                <div className="dashboard-products-list">
                  <PerfectScrollbar
                    option={MAIN_SCROLL_OPTIONS}
                  >
                    {popularitem.map((popular, index) => {
                      return (
                        <div
                          key={index}
                          className="popular-item d-flex align-items-start"
                        >
                          <NavLink
                            to="/app/layouts/details"
                            className="popular-item__thumb flex-shrink-0 rounded overflow-hidden"
                          >
                            <img
                              className="popular-item__img"
                              src={popular.preview}
                              alt={popular.alt}
                            />
                          </NavLink>
                          <div className="popular-item__body flex-grow-1">
                            <NavLink
                              to="/app/layouts/details"
                              className="popular-item__title d-block font-weight-medium text-body"
                            >
                              {popular.label}
                            </NavLink>
                            <p className="text-muted mt-1 text-small mb-2">
                              {popular.description}
                            </p>
                            <div className="popular-item__price text-small">
                              <del className="text-muted me-2">
                                {popular.oldprice}
                              </del>
                              <span className="font-weight-semibold">
                                {popular.newprice}
                              </span>
                            </div>
                            <div
                              className="popular-item__stars mt-2"
                              aria-hidden
                            >
                              <span className={`${popular.stars}`} />
                              <span className={`${popular.stars}`} />
                              <span className={`${popular.stars}`} />
                              <span className={`${popular.starshalf}`} />
                              <span className={`${popular.starsempty}`} />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </PerfectScrollbar>
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* Transctions Table Ends Here */}

          {/* Recent Activities start here */}
          <Col lg="6" md="12" className="mb-4">
            <Card>
              <CardBody>
                <CardTitle>
                  <IntlMessages id="main.recent-activities" />
                </CardTitle>
                <div className="dashboard-list-with-user">
                  <PerfectScrollbar
                    option={MAIN_SCROLL_OPTIONS}
                  >
                    {recentAct.map((act, index) => {
                      const total = act.total ?? 1;
                      const status = act.status ?? 0;
                      return (
                        <div
                          key={index}
                          className="d-flex flex-row mb-3 pb-3 border-bottom"
                        >
                          <NavLink to="/app/layouts/details">
                            <i className={`${act.icons}`} />
                          </NavLink>

                          <div className="pl-3 pr-2">
                            <div className="float-right">{act.time}</div>
                            <NavLink to="/app/layouts/details">
                              <p className="font-weight-medium mb-0 w-350">
                                {act.label}
                              </p>

                              <p className="text-muted mb-0 mt-1 text-small">
                                {act.subtitle}
                              </p>

                              <Progress
                                className="mt-3"
                                value={(status / total) * 100}
                              />
                              <p>
                                <span className="float-right text-muted">
                                  {act.reached}
                                </span>
                              </p>
                            </NavLink>
                          </div>
                        </div>
                      );
                    })}
                  </PerfectScrollbar>
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* Recent Activties End Here */}
        </Row>

        {/*  Recent Customers */}
        <Row>
          <Col xl="6" lg="12" md="12" className="mb-4">
            <Card className="h-100">
              <CardBody>
                <CardTitle>
                  <IntlMessages id="main.customers-status" />
                </CardTitle>
                <div className="dashboard-products-list">
                  <PerfectScrollbar
                    option={MAIN_SCROLL_OPTIONS}
                  >
                    {recentCustomer.map((recent, index) => {
                      return (
                        <div
                          key={index}
                          className="d-flex flex-row border-bottom"
                        >
                          <Table striped>
                            <tbody>
                              <tr>
                                <td className="border-right">
                                  <img
                                    src={recent.preview}
                                    alt={recent.alt}
                                    className="border-0 rounded-circle align-self-center w50px"
                                  />
                                  <p className="text-muted mt-2">
                                    {recent.time}
                                  </p>
                                </td>
                                <td>
                                  {recent.label}{" "}
                                  <p className="text-muted mt-2">
                                    <span className="fa fa-map-marker-alt" />{" "}
                                    {recent.location}
                                  </p>
                                  <span
                                    className={`${
                                      recent.colors
                                    }  fontsize10px pr-1 pl-1 text-white text-uppercase`}
                                  >
                                    {recent.customerState}
                                  </span>
                                </td>
                                <td className="text-muted w-100px" />
                              </tr>
                            </tbody>
                          </Table>
                        </div>
                      );
                    })}
                  </PerfectScrollbar>
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* Radar */}
          <Col xl="6" lg="12" md="12">
            <Card>
              <CardBody>
                <RadarMultipleSeries />
              </CardBody>
            </Card>
          </Col>

          {/* Radar Ends Here */}
        </Row>
      </Fragment>
  );
}
export default DefaultDashboard;
